/**
 * נייר השלמות — "why is my document blocked", as a first-class screen with its own address.
 * 09 decision 7, §5.7b.
 *
 * A blocked document is NOT an error (B1). It is a checklist a non-technical customer can finish
 * without calling the office — which is the whole reason this is a screen and not a toast.
 * B4: the draft is always producible even when the final is blocked; a CPA needs to see the shape
 * of the report before it is complete.
 */

import { useEffect, useState } from 'react';
import { useLocale } from '../i18n/LocaleProvider';
import { AppShell } from '../shell/AppShell';
import { useDeclareScreenContext, useWorkContext } from '../shell/ContextProvider';
import { BlockedReasons } from '../components/BlockedReasons';
import { Modal } from '../components/Modal';
import { StateBlock } from '../components/StateBlock';
import { fixtureBlockedDocument, fixturesEnabled } from '../api/fixtures';
import { routes } from '../routes';
import type { BlockedDocument } from '../api/contract';

export interface BlockedDocumentScreenProps {
  readonly companyUid: string;
  readonly documentUid: string;
}

export function BlockedDocumentScreen({
  companyUid,
  documentUid,
}: BlockedDocumentScreenProps): JSX.Element {
  const { t } = useLocale();
  const ctx = useWorkContext();
  const { setCompanyByUid, setEntityLabel } = useDeclareScreenContext();
  const [markingUid, setMarkingUid] = useState<string | null>(null);

  useEffect(() => {
    setCompanyByUid(companyUid);
    setEntityLabel(t('doc.title'));
  }, [companyUid, setCompanyByUid, setEntityLabel]);

  // Development fixture, clearly labelled. With the API running this is a fetch.
  const doc: BlockedDocument | null =
    fixturesEnabled() && documentUid === fixtureBlockedDocument.documentUid
      ? fixtureBlockedDocument
      : null;

  const back = {
    labelHe: ctx.company?.legalName ?? t('nav.companyHome'),
    href: routes.companyHome(companyUid),
  };

  if (doc === null) {
    return (
      <AppShell
      /* Every string here goes through `t()`; `translatedScreens.test.ts` fails the build
         if one Hebrew literal survives, which is what makes this a claim and not a note. */
      translated titleHe={t('doc.title')} backToParent={back}>
        <StateBlock
          titleHe={t('module.notFoundTitle')}
          bodyHe={t('doc.notFoundBody')}
          nextStep={<a className="btn" href={back.href}>{`${t('action.backTo')} ${back.labelHe}`}</a>}
        />
      </AppShell>
    );
  }

  return (
    <AppShell
      /* Every string here goes through `t()`; `translatedScreens.test.ts` fails the build
         if one Hebrew literal survives, which is what makes this a claim and not a note. */
      translated
      titleHe={`${t('doc.title')} — ${doc.companyLegalName} · ${doc.periodDisplayHe}`}
      backToParent={back}
    >
      <BlockedReasons
        blockers={doc.blockers}
        documentKindHe={doc.documentKindHe}
        onMarkNotRelevant={(uid) => setMarkingUid(uid)}
      />

      <div className="actions-row">
        {/* B4 — the draft is producible even while the final is blocked. */}
        {doc.draftAvailable ? (
          <button type="button" className="btn">
            {t('doc.draft')}
          </button>
        ) : null}
        {/* A verb. The result is a job with visible state (§5.7), never a silent download. */}
        <button type="button" className="btn btn--primary">
          {t('doc.retry')}
        </button>
      </div>

      {markingUid === null ? null : (
        // 09 §2.3 row 5 class: one decision, two fields, reversible by re-opening. A modal qualifies.
        // B5 — it records WHO marked it and WHY; suppressing a blocker silently would defeat the control.
        <Modal
          intentHe={t('doc.markIrrelevantTitle')}
          blastRadiusHe={`${t('doc.blastPrefix')} ${doc.companyLegalName} · ${doc.periodDisplayHe} — ${t('doc.blastSuffix')}`}
          fields={2}
          confirmLabelHe={t('doc.markIrrelevant')}
          onConfirm={() => setMarkingUid(null)}
          onClose={() => setMarkingUid(null)}
        >
          <label className="field">
            <span className="field__label">{t('common.reason')}</span>
            <textarea className="field__input" rows={3} />
          </label>
          <label className="field">
            <span className="field__label">{t('common.validUntil')}</span>
            <input className="field__input" type="date" dir="ltr" />
          </label>
        </Modal>
      )}
    </AppShell>
  );
}
